// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import PaginationControls from 'shared/components/common/PaginationControls';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddUpdateCustomerModal from 'main-app/components/AddUpdateCustomerModal';
import ExportCSVModal from 'main-app/components/ExportCSVModal';
import FilterControls, {
  type FilterValue,
} from 'main-app/components/FilterControls';
import CustomerList from './CustomerList';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';

const PAGE_SIZE = 30;

type Props = {
  history: RouterHistory,
};

type State = {
  addModalOpen: boolean,
  exportModalOpen: boolean,
  search: string,
  graphqlSearchValue: string,
  createAnotherCustomer: boolean,
  currentPage: number,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class Customers extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      addModalOpen: false,
      exportModalOpen: false,
      search: '',
      graphqlSearchValue: '',
      createAnotherCustomer: false,
      currentPage: 1,
    };

    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;

    if (!search) {
      return DEFAULT_FILTER;
    }

    return {
      ...DEFAULT_FILTER,
      ...qs.parse(search, { ignoreQueryPrefix: true }),
    };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;

    history.replace(
      '/customers?' +
        qs.stringify(filters, {
          encode: true,
          arrayFormat: 'brackets',
        }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;

    return (
      !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) || graphqlSearchValue
    );
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;

    const queryVariables = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: {
        field: filters.sort,
        direction: filters.direction,
      },
    };

    if (queryVariables.orderedAt) {
      const [start, end] = queryVariables.orderedAt.split(' - ');
      queryVariables.orderedAtStart = start;
      queryVariables.orderedAtEnd = end;

      delete queryVariables.orderedAt;
    }

    if (queryVariables.requiredAt) {
      const [start, end] = queryVariables.requiredAt.split(' - ');
      queryVariables.requiredAtStart = start;
      queryVariables.requiredAtEnd = end;

      delete queryVariables.requiredAt;
    }

    if (queryVariables.promisedAt) {
      const [start, end] = queryVariables.promisedAt.split(' - ');
      queryVariables.promisedAtStart = start;
      queryVariables.promisedAtEnd = end;

      delete queryVariables.promisedAt;
    }

    delete queryVariables.sort;
    delete queryVariables.direction;

    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState(
      {
        search,
      },
      this.handleDebouncedSearch,
    );
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;

    // Updating graphqlSearchValue will auto-kick-off a graphql query re-fetch
    this.setState({
      graphqlSearchValue: search,
      currentPage: 1,
    });

    Analytics.trackEvent('Search Applied', {
      listName: 'Customers',
      searchString: search,
    });
  };

  handleCSVExport = async (columns: string[]) => {
    const gqlQueryArgs = this.compileQueryVariables();

    Analytics.trackEvent('Export List', {
      page: 'Customer List',
    });
    window.open(
      `/api/v1/csv-export-customers?` +
        qs.stringify(
          {
            gqlQueryArgs,
            columns,
          },
          {
            encode: true,
            arrayFormat: 'brackets',
          },
        ),
    );
  };

  handleOpenCreateModal = () => {
    this.setState({
      addModalOpen: true,
    });
  };

  handleOpenExportModal = () => {
    this.setState({
      exportModalOpen: true,
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      addModalOpen: false,
      exportModalOpen: false,
      createAnotherCustomer: false,
    });
  };

  handleAddModalReopen = () => {
    this.setState(
      {
        addModalOpen: false,
        exportModalOpen: false,
        createAnotherCustomer: true,
      },
      () => {
        this.setState({
          addModalOpen: true,
        });
      },
    );
  };

  handlePageChange = (page: number) => {
    this.setState({ currentPage: page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  render() {
    const { history } = this.props;
    const {
      addModalOpen,
      exportModalOpen,
      search,
      createAnotherCustomer,
      currentPage,
    } = this.state;

    const offset = (currentPage - 1) * PAGE_SIZE;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{
          force: true,
        }}
        query={graphql`
          query CustomersQuery(
            $count: Int!
            $offset: Int
            $search: String
            $sortBy: CustomerSort
            $customerNumbers: [String!]
            $salesUserIds: [ID!]
          ) {
            customers(
              first: $count
              offset: $offset
              search: $search
              sortBy: $sortBy
              customerNumbers: $customerNumbers
              salesUserIds: $salesUserIds
            ) {
              totalCount
              edges {
                ...CustomerList_customerEdges
              }
            }

            viewer {
              id
              permissions
            }
          }
        `}
        variables={{
          ...this.compileQueryVariables(),
          count: PAGE_SIZE,
          offset,
        }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Customers')}
              exportButtonLabel={props && i18n.t('Export to CSV')}
              onExportButtonClick={this.handleOpenExportModal}
              createButtonLabel={
                props &&
                props.viewer.permissions.includes('CREATE_CUSTOMER') &&
                i18n.t('New Customer')
              }
              onCreateButtonClick={this.handleOpenCreateModal}
              searchPlaceholder={i18n.t('Customer Name or Customer #')}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              savedFilterType="CUSTOMER"
              onFiltersChange={filters => {
                this.setState({ currentPage: 1 });
                this.setFilters(filters);
              }}
            />
            {!props ? (
              <Loader />
            ) : !props.customers.edges.length && this.filtersDirty() ? (
              <EmptySearchResults />
            ) : (
              <>
                <CustomerList
                  history={history}
                  onCreateNew={this.handleOpenCreateModal}
                  customerEdges={props.customers.edges}
                  deleteEnabled
                  editEnabled={props.viewer.permissions.includes(
                    'UPDATE_CUSTOMER',
                  )}
                />
                <PaginationControls
                  currentPage={currentPage}
                  totalCount={props.customers.totalCount}
                  pageSize={PAGE_SIZE}
                  onPageChange={this.handlePageChange}
                />
              </>
            )}

            {addModalOpen && (
              <AddUpdateCustomerModal
                onClose={this.handleCloseAllModals}
                onSuccess={customer => history.push(`/customer/${customer.id}`)}
                onReopen={this.handleAddModalReopen}
                createAnotherCustomer={createAnotherCustomer}
                customer={null}
              />
            )}
            {exportModalOpen && (
              <ExportCSVModal
                onClose={this.handleCloseAllModals}
                title={i18n.t('Export Customers to CSV')}
                columnsApiUrl="/api/v1/csv-export-customers/columns"
                onSubmit={this.handleCSVExport}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default Customers;
